<script setup lang="ts">
import {
  Search as SearchIcon,
  Bike as BikeIcon,
  ChevronDown as ChevronDownIcon,
  Globe as GlobeIcon,
  Users as UsersIcon,
  EyeOff as EyeOffIcon,
  Pin as PinIcon,
  PinOff as PinOffIcon,
  Route as RouteIcon,
  Waypoints as WaypointsIcon,
  Camera as CameraIcon,
  Check as CheckIcon,
  Bookmark as BookmarkIcon,
  MoreHorizontal as MoreHorizontalIcon,
  X as XIcon,
} from '@lucide/vue'
import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'

import WorkspaceOverviewFilterControls from '@/components/map/WorkspaceOverviewFilterControls.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import BaseEmptyState from '@/components/ui/BaseEmptyState.vue'
import MobileActionSheet from '@/components/ui/MobileActionSheet.vue'
import { useEventListener } from '@/composables/useEventListener'
import { useSwipeAction } from '@/composables/useSwipeAction'
import { apiService, type WorkspaceSearchItem } from '@/services/apiService'
import { connectivityService } from '@/services/connectivityService'
import { showConfirm } from '@/services/dialogService'
import { parseLineString, calculateBounds } from '@/services/lineStringService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { MOBILE_PANEL_DRAG_START_EVENT } from '@/services/mobilePanelGestureService'
import { openMobileRenameSheet } from '@/services/mobileRenameService'
import { requireOnlineModificationAccess } from '@/services/offlineModificationGuardService'
import {
  isSignedUrlExpiring,
  PHOTO_ASSET_URL_EXPIRY_OFFSET_MS,
} from '@/services/photoAssetUrlService'
import { pinCollectionItems, unpinCollectionItems } from '@/services/pinnedService'
import { syncService } from '@/services/syncService'
import { showError, showSuccess } from '@/services/toastService'
import { TrackColorService } from '@/services/trackColorService'
import { isSmallScreen } from '@/services/uiService'
import { useAuthStore } from '@/stores/authStore'
import {
  useSearchWorkspaceStore,
  type SelectedSearchWorkspace,
} from '@/stores/searchWorkspaceStore'
import { useUiStore } from '@/stores/uiStore'
import { getWorkspaceAuthorByline } from '@/utils/workspaceAuthorByline'
import {
  applyWorkspaceOverviewFilters,
  buildWorkspaceOverviewResults,
  type WorkspaceOverviewOfflineFilter,
  type WorkspaceOverviewResult,
} from '@/utils/workspaceOverviewFilters'

import CollectionActionSheetContent from './CollectionActionSheetContent.vue'

const emit = defineEmits<{
  'select-workspace': [workspaceId: string, workspaceName: string]
  'make-offline': [workspaceId: string]
  'toggle-selection': [workspaceId: string]
}>()

const authStore = useAuthStore()
const searchWorkspaceStore = useSearchWorkspaceStore()
const uiStore = useUiStore()
const router = useRouter()

// True when running inside a Tauri (native app) build
const isTauriApp = Boolean(import.meta.env.TAURI_ENV_TARGET_TRIPLE)

// Offline filter chip state (Tauri builds only)
const offlineFilter = computed({
  get: () => uiStore.workspaceOverviewOfflineFilter,
  set: (value: WorkspaceOverviewOfflineFilter) => uiStore.setWorkspaceOverviewOfflineFilter(value),
})

const activeOfflineLabel = computed(() =>
  offlineFilter.value === 'downloaded' ? 'Downloaded' : 'Offline',
)

const panelChipBaseClass =
  'inline-flex items-center gap-1 rounded-full px-4 py-1.5 text-sm font-normal whitespace-nowrap shrink-0 transition-colors hover:cursor-pointer'
const panelChipActiveClass =
  'border border-gray-900/70 bg-white/92 text-gray-900 shadow-sm backdrop-blur-sm hover:bg-white'
const panelChipInactiveClass =
  'border border-black/[0.08] bg-white/92 text-gray-600 shadow-sm backdrop-blur-sm hover:bg-white hover:text-gray-900'
const panelChipMenuClass =
  'fixed z-9999 overflow-hidden rounded-xl border border-black/[0.08] bg-white/92 py-1 shadow-md backdrop-blur-sm'
const panelChipMenuOptionClass =
  'flex w-full items-center gap-2 px-3 py-2 text-left text-xs transition-colors hover:cursor-pointer'
const MOBILE_LONG_PRESS_MS = 500
const MOBILE_LONG_PRESS_MOVE_THRESHOLD_PX = 10
const MOBILE_PANEL_COLLAPSE_BEFORE_MENU_MS = 220

const getWorkspacePlaceholderStyle = (clientId: string) => {
  const backgroundColor = searchWorkspaceStore.generateWorkspaceColor(clientId)
  return {
    backgroundColor,
    color: TrackColorService.getTextColor(backgroundColor),
  }
}

const getWorkspaceDotColor = (clientId: string) =>
  searchWorkspaceStore.generateWorkspaceColor(clientId)

const offlineMenuOpen = ref(false)
const offlineMenuStyle = ref<{ top?: string; bottom?: string; left: string }>({
  top: '0px',
  left: '0px',
})

const toggleOfflineMenu = (event: MouseEvent) => {
  if (offlineMenuOpen.value) {
    offlineMenuOpen.value = false
    return
  }
  const button = event.currentTarget as HTMLElement
  const rect = button.getBoundingClientRect()
  const spaceBelow = window.innerHeight - rect.bottom
  if (spaceBelow >= 100) {
    offlineMenuStyle.value = { top: `${rect.bottom + 4}px`, left: `${rect.left}px` }
  } else {
    offlineMenuStyle.value = {
      bottom: `${window.innerHeight - rect.top + 4}px`,
      left: `${rect.left}px`,
    }
  }
  offlineMenuOpen.value = true
}

const closeOfflineMenu = () => {
  offlineMenuOpen.value = false
}

const selectOfflineOption = (value: 'all' | 'downloaded') => {
  offlineFilter.value = value
  offlineMenuOpen.value = false
}

const workspaces = ref<WorkspaceSearchItem[]>([])
const isLoading = ref(false)
const hasError = ref(false)
const panelRootRef = ref<HTMLElement | null>(null)
const vAutoFocus = {
  mounted(el: HTMLInputElement) {
    el.focus()
    el.select()
  },
}

const activeMenuWorkspaceId = ref<string | null>(null)
const mobileCollectionMenuOpen = ref(false)
const menuStyle = ref<{ top?: string; bottom?: string; right?: string; left?: string }>({
  top: '0px',
  right: '0px',
})
const mobileLongPressTimer = ref<ReturnType<typeof setTimeout> | null>(null)
const mobileLongPressActivated = ref(false)
const mobileLongPressStartPoint = ref<{ x: number; y: number } | null>(null)
const editingWorkspaceId = ref<string | null>(null)
const editingWorkspaceName = ref('')
const editingWorkspaceError = ref('')

// Delay loading icon to avoid flicker on fast loads
const showLoadingIcon = ref(false)
let loadingIconTimer: number | null = null

const sortBy = computed({
  get: () => uiStore.workspaceSearchSortBy,
  set: (value: 'name' | 'date' | 'modified') => uiStore.setWorkspaceSearchSortBy(value),
})

const sortDirection = computed({
  get: () => uiStore.workspaceSearchSortDirection,
  set: (value: 'asc' | 'desc') => uiStore.setWorkspaceSearchSortDirection(value),
})

const closeRowMenu = () => {
  if (activeMenuWorkspaceId.value !== null && isSmallScreen.value) {
    searchWorkspaceStore.setHoveredWorkspaceId(null)
  }
  if (isSmallScreen.value && mobileCollectionMenuOpen.value) {
    mobileCollectionMenuOpen.value = false
    return
  }
  activeMenuWorkspaceId.value = null
}

const handleMobileCollectionMenuClosed = () => {
  activeMenuWorkspaceId.value = null
  searchWorkspaceStore.setHoveredWorkspaceId(null)
}

const MENU_HEIGHT_ESTIMATE = 220

const openRowMenuAtElement = (workspaceId: string, anchor: HTMLElement) => {
  if (isSmallScreen.value && panelRootRef.value) {
    activeMenuWorkspaceId.value = workspaceId
    mobileCollectionMenuOpen.value = true
    return
  }
  const rect = anchor.getBoundingClientRect()
  const spaceBelow = window.innerHeight - rect.bottom
  const right = window.innerWidth - rect.right
  if (spaceBelow >= MENU_HEIGHT_ESTIMATE) {
    menuStyle.value = { top: `${rect.bottom + 4}px`, right: `${right}px` }
  } else {
    menuStyle.value = { bottom: `${window.innerHeight - rect.top + 4}px`, right: `${right}px` }
  }
  activeMenuWorkspaceId.value = workspaceId
}

const toggleRowMenu = (workspaceId: string, event: MouseEvent) => {
  if (activeMenuWorkspaceId.value === workspaceId) {
    activeMenuWorkspaceId.value = null
    return
  }
  const button = event.currentTarget as HTMLElement
  openRowMenuAtElement(workspaceId, button)
}

const getWorkspaceById = (workspaceId: string) =>
  workspaces.value.find((workspace) => workspace.clientId === workspaceId)

const canRenameWorkspaceResult = (result: (typeof searchResults.value)[0]) => result.canEdit

const canDeleteWorkspaceResult = (result: (typeof searchResults.value)[0]) => result.isOwner

const isCollectionPinned = (result: (typeof searchResults.value)[0]) => {
  if (result.isPinned === true) return true
  if (result.isPinned === false) return false
  const workspace = getWorkspaceById(result.clientId)
  const tours = workspace?.tours ?? []
  const hasOtherItems = (result.poiCount ?? 0) > 0 || (result.photoCount ?? 0) > 0
  return tours.length > 0 && !hasOtherItems && tours.every((tour) => tour.isPinned === true)
}

const getAuthorByline = (result: (typeof searchResults.value)[0]): string | null => {
  return getWorkspaceAuthorByline({
    isOwner: result.isOwner,
    ownerNames: result.ownerNames,
    currentUserName: authStore.user?.name,
  })
}

const isWorkspaceHidden = (workspaceId: string) => !isWorkspaceVisible(workspaceId)

const centerMapOnWorkspace = (workspaceId: string, options: { closeMenu?: boolean } = {}) => {
  const workspace = getWorkspaceById(workspaceId)
  if (!workspace) return
  const bounds = calculateWorkspaceBounds(workspace)
  if (!bounds) return
  uiStore.beginMapNavigation('explicit')
  window.dispatchEvent(
    new CustomEvent('zoom-to-search-workspaces', { detail: { bounds, force: true } }),
  )
  if (options.closeMenu !== false) {
    closeRowMenu()
  }
}

const openShareForWorkspace = async (result: (typeof searchResults.value)[0]) => {
  closeRowMenu()

  try {
    const loaded = await syncService.loadRemoteWorkspace(result.clientId, false)
    if (!loaded) return

    uiStore.requestWorkspaceShareOpen()
    uiStore.beginMapNavigation('collection-route')
    await router.push({ name: 'workspace-detail', params: { workspaceId: result.clientId } })
    window.dispatchEvent(new CustomEvent('open-workspace-share-modal'))
  } catch (error) {
    showError(
      `Failed to open share modal: ${error instanceof Error ? error.message : String(error)}`,
    )
  }
}

const openMakeOfflineDialog = (workspaceId: string) => {
  closeRowMenu()
  emit('make-offline', workspaceId)
}

const openRenameWorkspace = async (result: (typeof searchResults.value)[0]) => {
  closeRowMenu()
  if (!canRenameWorkspaceResult(result)) return

  if (isSmallScreen.value) {
    if (!(await requireOnlineModificationAccess('rename workspace'))) return
    const workspaceId = result.clientId
    openMobileRenameSheet({
      title: 'Rename collection',
      currentName: result.title,
      submit: async (name) => {
        const entry = workspaces.value.find((workspace) => workspace.clientId === workspaceId)
        if (!entry || !entry.canEdit || entry.name === name) return
        await apiService.renameWorkspace(workspaceId, name)
        entry.name = name
      },
    })
    return
  }

  editingWorkspaceId.value = result.clientId
  editingWorkspaceName.value = result.title
  editingWorkspaceError.value = ''
}

const cancelInlineRename = () => {
  editingWorkspaceId.value = null
  editingWorkspaceName.value = ''
  editingWorkspaceError.value = ''
}

const saveWorkspaceRename = async () => {
  const workspaceId = editingWorkspaceId.value
  if (!workspaceId) return
  const entry = workspaces.value.find((w) => w.clientId === workspaceId)
  if (!entry || !entry.canEdit) {
    cancelInlineRename()
    return
  }

  if (!(await requireOnlineModificationAccess('rename workspace'))) return

  const trimmed = editingWorkspaceName.value.trim()
  if (!trimmed) {
    editingWorkspaceError.value = 'Name cannot be empty'
    return
  }

  try {
    await apiService.renameWorkspace(workspaceId, trimmed)
    entry.name = trimmed
    cancelInlineRename()
  } catch (error) {
    showError(
      `Failed to rename workspace: ${error instanceof Error ? error.message : String(error)}`,
    )
  }
}

const deleteWorkspaceFromRow = async (result: (typeof searchResults.value)[0]) => {
  closeRowMenu()
  if (!canDeleteWorkspaceResult(result)) return
  if (!(await requireOnlineModificationAccess('delete workspace'))) return

  const confirmed = await showConfirm({
    title: 'Delete Workspace?',
    message: `Are you sure you want to delete "${result.title}"?\n\nThe collection will be marked for deletion and permanently removed after 7 days.\n\nOnly owners can access the workspace during this period.`,
    confirmText: 'Delete Workspace',
    cancelText: 'Cancel',
    variant: 'danger',
  })

  if (!confirmed) return

  try {
    await apiService.deleteWorkspace(result.clientId)
    await loadWorkspaces()
  } catch (error) {
    showError(
      `Failed to delete workspace: ${error instanceof Error ? error.message : String(error)}`,
    )
  }
}

const unbookmarkWorkspaceFromRow = async (result: (typeof searchResults.value)[0]) => {
  closeRowMenu()
  const wasBookmarkOnly = result.isBookmarkOnly

  try {
    await apiService.unbookmarkWorkspace(result.clientId)
    const workspace = workspaces.value.find((w) => w.clientId === result.clientId)
    if (workspace) workspace.isBookmarked = false
    searchWorkspaceStore.updateWorkspaceBookmark(result.clientId, false)

    if (wasBookmarkOnly) {
      workspaces.value = workspaces.value.filter((w) => w.clientId !== result.clientId)
      searchWorkspaceStore.removeWorkspaceFromCatalog(result.clientId)
    }
  } catch (error) {
    showError(
      `Failed to remove bookmark: ${error instanceof Error ? error.message : String(error)}`,
    )
  }
}

const pinCollectionFromRow = async (result: (typeof searchResults.value)[0]) => {
  closeRowMenu()
  if (!(await requireOnlineModificationAccess('pin collection'))) return

  try {
    const detail = await apiService.getWorkspaceDetail(result.clientId)
    await pinCollectionItems(detail)
    searchWorkspaceStore.updateWorkspacePinned(result.clientId, true)
    showSuccess('Pinned collection')
    await loadWorkspaces()
  } catch (error) {
    showError(`Failed to pin collection: ${error instanceof Error ? error.message : String(error)}`)
  }
}

const unpinCollectionFromRow = async (result: (typeof searchResults.value)[0]) => {
  closeRowMenu()
  if (!(await requireOnlineModificationAccess('unpin collection'))) return

  try {
    const detail = await apiService.getWorkspaceDetail(result.clientId)
    await unpinCollectionItems(detail)
    searchWorkspaceStore.updateWorkspacePinned(result.clientId, false)
    showSuccess('Unpinned collection')
    await loadWorkspaces()
  } catch (error) {
    showError(
      `Failed to unpin collection: ${error instanceof Error ? error.message : String(error)}`,
    )
  }
}

const toggleCollectionPinnedFromRow = async (result: (typeof searchResults.value)[0]) => {
  if (isCollectionPinned(result)) {
    await unpinCollectionFromRow(result)
    return
  }
  await pinCollectionFromRow(result)
}

// Event handlers for search tab behavior
const handleFirstSearchVisit = () => {
  searchWorkspaceStore.activateAllWorkspaces()
}

const handleSearchTabLeave = () => {
  searchWorkspaceStore.hideAllWorkspacesTemporary()
}

const handleSearchTabEnter = () => {
  searchWorkspaceStore.restoreWorkspaceVisibility()

  const bounds = searchWorkspaceStore.getVisibleWorkspacesBounds()
  if (bounds) {
    window.dispatchEvent(new CustomEvent('zoom-to-search-workspaces', { detail: bounds }))
  }
}

// Add keyboard shortcuts for bulk visibility actions
const handleKeydown = (event: KeyboardEvent) => {
  // Only handle shortcuts when in collections overview mode
  if (!uiStore.isInWorkspacesOverview) return
  const key = typeof event.key === 'string' ? event.key.toLowerCase() : ''
  const target = event.target instanceof HTMLElement ? event.target : null
  const isTyping =
    target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable
  if (isTyping) return

  // S — toggle sync-to-map filter (no modifier, no typing)
  if (event.ctrlKey) {
    switch (key) {
      case 'h':
        event.preventDefault()
        if (canHideAll.value) {
          searchWorkspaceStore.hideAllWorkspaces()
        }
        break
      case 'u':
        event.preventDefault()
        if (canShowAll.value) {
          searchWorkspaceStore.showSelectedWorkspaces()
        }
        break
    }
  }
}

// Set up event listeners for search tab behavior using Vue composable
useEventListener(window, 'search-tab-first-visit', handleFirstSearchVisit)
useEventListener(window, 'search-tab-leave', handleSearchTabLeave)
useEventListener(window, 'search-tab-enter', handleSearchTabEnter)
useEventListener(window, 'keydown', handleKeydown)
useEventListener(document, 'click', (event: Event) => {
  const target = event.target as HTMLElement | null
  if (!target?.closest('[data-workspace-row-menu]')) {
    closeRowMenu()
  }
  if (!target?.closest('[data-sort-menu]')) {
    closeSortMenu()
  }
  if (!target?.closest('[data-offline-menu]')) {
    closeOfflineMenu()
  }
})

// Load workspaces on component mount
onMounted(async () => {
  // The collections search bar was removed; clear any persisted query so a stale
  // value can't silently filter the collections list/map with no UI to reset it.
  if (uiStore.workspaceSearchQuery) {
    uiStore.setWorkspaceSearchQuery('')
  }
  // Initialize the search workspace store first
  await searchWorkspaceStore.init()
  await loadWorkspaces()
})

// Show BikeIcon only after 800ms of continuous loading to prevent flicker
watch(isLoading, (loading) => {
  if (loading) {
    if (loadingIconTimer) {
      clearTimeout(loadingIconTimer)
    }
    loadingIconTimer = window.setTimeout(() => {
      if (isLoading.value) {
        showLoadingIcon.value = true
      }
    }, 800)
  } else {
    if (loadingIconTimer) {
      clearTimeout(loadingIconTimer)
      loadingIconTimer = null
    }
    showLoadingIcon.value = false
  }
})

const loadWorkspaces = async () => {
  isLoading.value = true
  hasError.value = false
  try {
    // Only fetch workspaces if user is authenticated
    if (authStore.isAuthenticated) {
      // Always reload when My Workspaces becomes visible
      const raw = await searchWorkspaceStore.ensureWorkspacesLoaded(true)
      // Route through setWorkspaceCatalog so cover photo thumbnail URLs are resolved
      searchWorkspaceStore.setWorkspaceCatalog({
        workspaces: raw,
        personalTourCount: searchWorkspaceStore.personalTourCount,
      })
      workspaces.value = searchWorkspaceStore.allWorkspaces
      // Activate all workspaces to make them visible by default
      searchWorkspaceStore.activateAllWorkspaces()
      hasError.value = false
    } else {
      workspaces.value = []
      searchWorkspaceStore.setWorkspaceCatalog({ workspaces: [], personalTourCount: 0 })
      hasError.value = false
    }
  } catch (error) {
    showError(
      `Failed to load workspaces: ${error instanceof Error ? error.message : String(error)}`,
    )
    workspaces.value = []
    hasError.value = true
  } finally {
    isLoading.value = false
  }
}

const formatDateRange = (start?: string | null, end?: string | null): string | null => {
  if (!start && !end) return null
  const fmtFull = (d: Date) =>
    d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
  const fmtNoYear = (d: Date) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  if (start && end) {
    const s = new Date(start)
    const e = new Date(end)
    if (s.toDateString() === e.toDateString()) return fmtFull(s)
    if (s.getFullYear() === e.getFullYear()) return `${fmtNoYear(s)} – ${fmtFull(e)}`
    return `${fmtFull(s)} – ${fmtFull(e)}`
  }
  const d = new Date(start ?? end!)
  return fmtFull(d)
}

// Search results computed property
const searchResults = computed(() => {
  return buildWorkspaceOverviewResults(workspaces.value, {
    query: '',
    sortBy: sortBy.value,
    sortDirection: sortDirection.value,
  })
})

const applyOverviewFilters = (results: typeof searchResults.value) => {
  return applyWorkspaceOverviewFilters(results, {
    sharingFilter: uiStore.workspaceOverviewSharingFilter,
    offlineFilter: offlineFilter.value,
    isTauriApp,
  })
  // Offline/downloaded filter (Tauri only — isDownloaded field added when API supports it)
}

const mapFilteredResults = computed(() => applyOverviewFilters(searchResults.value))

const displayedResults = computed(() => mapFilteredResults.value)

const activeMenuResult = computed<WorkspaceOverviewResult | null>(() => {
  if (!activeMenuWorkspaceId.value) return null
  return (
    displayedResults.value.find((result) => result.clientId === activeMenuWorkspaceId.value) ??
    searchResults.value.find((result) => result.clientId === activeMenuWorkspaceId.value) ??
    null
  )
})

// Cover-photo thumbnails are signed URLs that expire. The overview loads them once on mount,
// so a long-open panel — or a client-side filter change like "Personal" that surfaces rows whose
// image never rendered — can show broken/expired thumbnails. Re-fetch the catalog to mint fresh
// signed URLs whenever any *displayed* cover is at/near expiry (mirrors the per-collection
// PhotoList refresh). The allWorkspaces watcher propagates the fresh URLs into workspaces.value →
// displayedResults → <img src>, so no manual reassignment is needed. Guarded so overlapping
// triggers (interval, filter change, <img @error>) collapse into a single in-flight refetch.
let coverRefreshInFlight = false
let coverRefreshIntervalId: number | null = null

const refreshCoverPhotoUrlsIfNeeded = async () => {
  if (coverRefreshInFlight) return
  if (!authStore.isAuthenticated || !connectivityService.isEffectivelyOnline.value) return

  const hasExpiringCover = displayedResults.value.some((result) =>
    isSignedUrlExpiring(result.coverPhotoThumbnailUrl),
  )
  if (!hasExpiringCover) return

  coverRefreshInFlight = true
  try {
    await searchWorkspaceStore.ensureWorkspacesLoaded(true)
  } catch (error) {
    logService.warn(LOG_CATEGORIES.API, 'Failed to refresh collection cover photo URLs', { error })
  } finally {
    coverRefreshInFlight = false
  }
}

// Time-based expiry while the panel stays open (matches PhotoList's cadence).
onMounted(() => {
  coverRefreshIntervalId = window.setInterval(
    () => void refreshCoverPhotoUrlsIfNeeded(),
    PHOTO_ASSET_URL_EXPIRY_OFFSET_MS,
  )
})

onUnmounted(() => {
  if (coverRefreshIntervalId !== null) {
    window.clearInterval(coverRefreshIntervalId)
    coverRefreshIntervalId = null
  }
})

// The displayed set changing (e.g. the "Personal" sharing filter) can surface rows whose expired
// cover image never rendered — refresh immediately rather than waiting for the interval tick.
watch(
  () => displayedResults.value.map((result) => result.clientId).join('|'),
  () => void refreshCoverPhotoUrlsIfNeeded(),
)

// Computed properties for bulk action button states
const canShowAll = computed(() => {
  return (
    searchWorkspaceStore.selectedWorkspaces.length > searchWorkspaceStore.visibleWorkspaces.length
  )
})

const canHideAll = computed(() => {
  return searchWorkspaceStore.visibleWorkspaces.length > 0
})

// Helper function to calculate bounding box for a single workspace
const calculateWorkspaceBounds = (workspace: WorkspaceSearchItem) => {
  const coordinateArrays: [number, number][][] = []

  workspace.tours.forEach((tour) => {
    tour.tracks.forEach((track) => {
      if (track.pointsLineReduced500m) {
        const coordinates = parseLineString(track.pointsLineReduced500m)
        if (coordinates.length > 0) {
          coordinateArrays.push(coordinates)
        }
      }
    })
  })

  return calculateBounds(coordinateArrays)
}

const handleResultClick = async (result: (typeof searchResults.value)[0]) => {
  if (props.selectionMode) {
    emit('toggle-selection', result.clientId)
    return
  }
  if (editingWorkspaceId.value) return
  if (collectionSwipe.consumeSuppressedClick()) return
  if (mobileLongPressActivated.value) {
    mobileLongPressActivated.value = false
    return
  }
  if (result.type === 'workspace') {
    // Find the workspace data
    const workspace = workspaces.value.find(
      (p: WorkspaceSearchItem) => p.clientId === result.clientId,
    )
    if (workspace) {
      uiStore.beginMapNavigation('collection-route')
      const bounds = calculateWorkspaceBounds(workspace)

      if (bounds) {
        // Start moving immediately from the catalog geometry while the full collection loads.
        // The route loader refines the fit once the final content and panel layout are ready.
        window.dispatchEvent(
          new CustomEvent('zoom-to-search-workspaces', {
            detail: { bounds, force: true, animate: true, updateOverviewBounds: false },
          }),
        )
      }

      emit('select-workspace', result.clientId, result.title)
    } else {
      // Fallback: emit with just the title
      uiStore.beginMapNavigation('collection-route')
      emit('select-workspace', result.clientId, result.title)
    }
  }
}

const cancelMobileLongPress = () => {
  if (mobileLongPressTimer.value !== null) {
    clearTimeout(mobileLongPressTimer.value)
    mobileLongPressTimer.value = null
  }
  mobileLongPressStartPoint.value = null
}

const collectionSwipe = useSwipeAction<(typeof searchResults.value)[0]>({
  getId: (result) => result.clientId,
  isDisabled: () => !isSmallScreen.value || editingWorkspaceId.value !== null,
  onCancelCompetingGesture: () => {
    cancelMobileLongPress()
    searchWorkspaceStore.setHoveredWorkspaceId(null)
  },
  onTrigger: (result) => toggleCollectionPinnedFromRow(result),
})

const triggerMobileLongPress = (result: (typeof searchResults.value)[0], anchor: HTMLElement) => {
  mobileLongPressTimer.value = setTimeout(() => {
    mobileLongPressTimer.value = null
    mobileLongPressActivated.value = true
    if (typeof navigator.vibrate === 'function') {
      navigator.vibrate(30)
    }
    const finishLongPressAction = () => {
      centerMapOnWorkspace(result.clientId, { closeMenu: false })
      openRowMenuAtElement(result.clientId, anchor)
    }

    if (isSmallScreen.value && uiStore.isMobileBottomPanelHigh) {
      window.dispatchEvent(new CustomEvent('workspace-mobile-panel-request-expanded'))
      window.setTimeout(finishLongPressAction, MOBILE_PANEL_COLLAPSE_BEFORE_MENU_MS)
      return
    }

    finishLongPressAction()
  }, MOBILE_LONG_PRESS_MS)
}

const onRowTouchStart = (event: TouchEvent, result: (typeof searchResults.value)[0]) => {
  if (!isSmallScreen.value || editingWorkspaceId.value || event.touches.length !== 1) return
  if (activeMenuWorkspaceId.value === null || activeMenuWorkspaceId.value === result.clientId) {
    handleRowMouseEnter(result.clientId)
  } else {
    searchWorkspaceStore.setHoveredWorkspaceId(null)
  }
  const touch = event.touches[0]
  mobileLongPressStartPoint.value = { x: touch.clientX, y: touch.clientY }
  collectionSwipe.start(event, result)
  triggerMobileLongPress(result, event.currentTarget as HTMLElement)
}

const onRowTouchMove = (event: TouchEvent, result: (typeof searchResults.value)[0]) => {
  collectionSwipe.move(event, result)
  if (!isSmallScreen.value || !mobileLongPressStartPoint.value || event.touches.length !== 1) return
  const touch = event.touches[0]
  const dx = touch.clientX - mobileLongPressStartPoint.value.x
  const dy = touch.clientY - mobileLongPressStartPoint.value.y
  if (
    Math.abs(dx) > MOBILE_LONG_PRESS_MOVE_THRESHOLD_PX ||
    Math.abs(dy) > MOBILE_LONG_PRESS_MOVE_THRESHOLD_PX
  ) {
    cancelMobileLongPress()
    searchWorkspaceStore.setHoveredWorkspaceId(null)
  }
}

const onRowTouchEnd = (result: (typeof searchResults.value)[0]) => {
  collectionSwipe.end(result)
  cancelMobileLongPress()
}

const onRowTouchCancel = (result: (typeof searchResults.value)[0]) => {
  collectionSwipe.cancel(result)
  cancelMobileLongPress()
  searchWorkspaceStore.setHoveredWorkspaceId(null)
}

const cancelCollectionContextMenuForPanelDrag = () => {
  cancelMobileLongPress()
  mobileLongPressActivated.value = false
  searchWorkspaceStore.setHoveredWorkspaceId(null)
  closeRowMenu()
}

useEventListener(window, MOBILE_PANEL_DRAG_START_EVENT, cancelCollectionContextMenuForPanelDrag)

// Visibility toggle functions
const toggleWorkspaceVisibility = (workspaceId: string) => {
  // Check if workspace is in selectedWorkspaces already
  const existingWorkspace = searchWorkspaceStore.selectedWorkspaces.find(
    (c: SelectedSearchWorkspace) => c.clientId === workspaceId,
  )

  if (existingWorkspace) {
    // Use the store's toggle function
    searchWorkspaceStore.toggleWorkspaceVisibility(workspaceId)
  } else {
    // Workspace not in selected workspaces yet, add it first
    const workspace = workspaces.value.find((p: WorkspaceSearchItem) => p.clientId === workspaceId)
    if (workspace) {
      searchWorkspaceStore.addWorkspace(workspace)
    }
  }
}

const isWorkspaceVisible = (workspaceId: string) => {
  const workspace = searchWorkspaceStore.selectedWorkspaces.find(
    (c: SelectedSearchWorkspace) => c.clientId === workspaceId,
  )
  return workspace?.isVisible ?? false
}

// Sort context menu
const sortMenuOpen = ref(false)
const sortMenuStyle = ref<{ top?: string; bottom?: string; right: string }>({
  top: '0px',
  right: '0px',
})

type SortByValue = 'name' | 'date' | 'modified'
type SortOption = {
  label: string
  chipLabel?: string
  sortBy: SortByValue
  sortDirection: 'asc' | 'desc'
}
const defaultSortOption = {
  sortBy: 'modified' as const,
  sortDirection: 'desc' as const,
}
const sortOptions: SortOption[] = [
  { label: 'Name (A to Z)', sortBy: 'name', sortDirection: 'asc' },
  { label: 'Name (Z to A)', sortBy: 'name', sortDirection: 'desc' },
  {
    label: 'Activity date (new to old)',
    chipLabel: 'New to old',
    sortBy: 'date',
    sortDirection: 'desc',
  },
  {
    label: 'Activity date (old to new)',
    chipLabel: 'Old to new',
    sortBy: 'date',
    sortDirection: 'asc',
  },
  { label: 'Last modified (default)', sortBy: 'modified', sortDirection: 'desc' },
  { label: 'Least recently modified', sortBy: 'modified', sortDirection: 'asc' },
]

const isActiveSort = (option: SortOption) =>
  sortBy.value === option.sortBy && sortDirection.value === option.sortDirection

const isDefaultSort = computed(
  () =>
    sortBy.value === defaultSortOption.sortBy &&
    sortDirection.value === defaultSortOption.sortDirection,
)

const activeSortLabel = computed(() => {
  if (isDefaultSort.value) return 'Sort'
  const activeOption = sortOptions.find((option) => isActiveSort(option))
  return activeOption?.chipLabel ?? activeOption?.label ?? 'Sort'
})

const toggleSortMenu = (event: MouseEvent) => {
  if (sortMenuOpen.value) {
    sortMenuOpen.value = false
    return
  }
  const button = event.currentTarget as HTMLElement
  const rect = button.getBoundingClientRect()
  const spaceBelow = window.innerHeight - rect.bottom
  const right = window.innerWidth - rect.right
  if (spaceBelow >= 260) {
    sortMenuStyle.value = { top: `${rect.bottom + 4}px`, right: `${right}px` }
  } else {
    sortMenuStyle.value = { bottom: `${window.innerHeight - rect.top + 4}px`, right: `${right}px` }
  }
  sortMenuOpen.value = true
}

const selectSortOption = (option: SortOption) => {
  sortBy.value = option.sortBy
  sortDirection.value = option.sortDirection
  sortMenuOpen.value = false
}

const closeSortMenu = () => {
  sortMenuOpen.value = false
}

const openLoginModal = () => {
  window.dispatchEvent(new CustomEvent('open-auth-modal', { detail: { view: 'login' } }))
}

// Row element refs for hover-driven scroll-to
const rowRefs = new Map<string, HTMLElement>()
const resultsListRef = ref<HTMLElement | null>(null)

// Rebuild the lookup map only when the visible result list actually changes,
// not on every render pass. flush:'post' ensures the DOM is up to date.
// (Declared below after createdResults/sharedResults are defined.)

const handleRowMouseEnter = (clientId: string) => {
  searchWorkspaceStore.setHoveredWorkspaceId(clientId)
}

const handleRowMouseLeave = (clientId: string) => {
  if (searchWorkspaceStore.hoveredWorkspaceId === clientId) {
    searchWorkspaceStore.setHoveredWorkspaceId(null)
  }
}

// Watch hover state driven by the map and scroll the matching row into view
watch(
  () => searchWorkspaceStore.hoveredWorkspaceId,
  (id) => {
    if (!id) return
    const el = rowRefs.get(id)
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  },
)

// Rebuild the rowRefs lookup map only when the visible result list actually changes,
// not on every render pass. flush:'post' ensures the DOM is up to date.
watch(
  displayedResults,
  () => {
    rowRefs.clear()
    resultsListRef.value?.querySelectorAll<HTMLElement>('[data-workspace-id]').forEach((el) => {
      const id = el.dataset.workspaceId
      if (id) rowRefs.set(id, el)
    })
  },
  { flush: 'post', immediate: true },
)

watch(
  () => searchWorkspaceStore.allWorkspaces,
  (nextWorkspaces) => {
    if (!authStore.isAuthenticated) return
    if (workspaces.value === nextWorkspaces) return

    workspaces.value = nextWorkspaces
    hasError.value = false
  },
)

// Watch type filter and sort — sync immediately
// Sync overview counts to store so parent headers can display them
watch(
  () => [displayedResults.value.length, mapFilteredResults.value.length] as const,
  ([displayed, total]) => {
    searchWorkspaceStore.setOverviewCounts(displayed, total)
  },
  { immediate: true },
)

watch(
  () => uiStore.isInWorkspacesOverview,
  (isOverview) => {
    if (!isOverview) {
      closeRowMenu()
    }
  },
)

// Clean up on unmount
onUnmounted(() => {
  if (loadingIconTimer) {
    clearTimeout(loadingIconTimer)
    loadingIconTimer = null
  }
})

// Watch for authentication state changes and reload workspaces
watch(
  () => authStore.isAuthenticated,
  async (isAuthenticated, wasAuthenticated) => {
    if (isAuthenticated) {
      // User logged in, reload workspaces (tab switching handled in MapHeader)
      await loadWorkspaces()
      // Auto-zoom to collections only on an actual login transition (false -> true),
      // NOT on the immediate fire when the component mounts (where wasAuthenticated
      // is undefined). This prevents forced re-zooming every time the drawer panel
      // expands and mounts this component.
      const isActualLogin = wasAuthenticated === false
      if (
        isActualLogin &&
        uiStore.isInWorkspacesOverview &&
        searchWorkspaceStore.hasSelectedWorkspaces
      ) {
        const bounds = searchWorkspaceStore.getVisibleWorkspacesBounds()
        if (bounds) {
          window.dispatchEvent(
            new CustomEvent('zoom-to-search-workspaces', { detail: { bounds, force: true } }),
          )
        }
      }
    } else {
      // User logged out, clear workspaces
      workspaces.value = []
      searchWorkspaceStore.setWorkspaceCatalog({ workspaces: [], personalTourCount: 0 })
      searchWorkspaceStore.clearAllWorkspaces()
      hasError.value = false
    }
  },
  { immediate: true },
)

const props = withDefaults(
  defineProps<{
    fillContainer?: boolean
    selectionMode?: boolean
    selectedCollectionIds?: Set<string>
  }>(),
  { fillContainer: false, selectionMode: false },
)
</script>

<template>
  <div ref="panelRootRef" :class="['flex flex-col', props.fillContainer ? 'h-full' : '']">
    <!-- Actions Row — search bar + filter chips, pinned above the scrollable list -->
    <div data-actions-row data-mobile-panel-drag-surface class="flex flex-col shrink-0 bg-white">
      <!-- Filter Chip Bar (no longer in scroll container) -->
      <div class="bg-white border-b border-gray-200 shrink-0 py-2">
        <div
          class="flex w-full items-center gap-1.5"
          :class="
            isSmallScreen
              ? 'overflow-x-auto overflow-y-hidden flex-nowrap -mx-3 pl-6 pr-3'
              : 'flex-wrap px-3'
          "
        >
          <!-- Source dropdown chip -->
          <WorkspaceOverviewFilterControls variant="panel" />

          <!-- Offline chip (Tauri builds only) -->
          <div v-if="isTauriApp" class="relative shrink-0" data-offline-menu>
            <button
              @click="toggleOfflineMenu($event)"
              :class="[
                panelChipBaseClass,
                offlineFilter !== 'all' ? panelChipActiveClass : panelChipInactiveClass,
              ]"
            >
              {{ activeOfflineLabel }}
              <ChevronDownIcon class="h-3 w-3 shrink-0" />
            </button>
            <Teleport to="body">
              <div
                v-if="offlineMenuOpen"
                :style="offlineMenuStyle"
                data-offline-menu
                :class="[panelChipMenuClass, 'w-40']"
              >
                <button
                  v-for="option in [
                    { label: 'All', value: 'all' },
                    { label: 'Downloaded', value: 'downloaded' },
                  ] as const"
                  :key="option.value"
                  @click="selectOfflineOption(option.value)"
                  :class="[
                    panelChipMenuOptionClass,
                    offlineFilter === option.value
                      ? 'text-emerald-700 bg-emerald-50 font-medium'
                      : 'text-gray-700 hover:bg-gray-50',
                  ]"
                >
                  <CheckIcon
                    v-if="offlineFilter === option.value"
                    class="w-3.5 h-3.5 text-emerald-600 shrink-0"
                  />
                  <span v-else class="w-3.5 h-3.5 shrink-0" />
                  {{ option.label }}
                </button>
              </div>
            </Teleport>
          </div>

          <!-- Sort chip -->
          <div class="relative shrink-0" data-sort-menu>
            <button
              @click="toggleSortMenu($event)"
              :class="[
                panelChipBaseClass,
                sortMenuOpen || !isDefaultSort ? panelChipActiveClass : panelChipInactiveClass,
              ]"
            >
              {{ activeSortLabel }}
              <ChevronDownIcon class="h-3 w-3 shrink-0" />
            </button>
            <Teleport to="body">
              <div
                v-if="sortMenuOpen"
                :style="sortMenuStyle"
                data-sort-menu
                :class="[panelChipMenuClass, 'w-56']"
              >
                <button
                  v-for="option in sortOptions"
                  :key="`${option.sortBy}-${option.sortDirection}`"
                  @click="selectSortOption(option)"
                  :class="[
                    panelChipMenuOptionClass,
                    isActiveSort(option)
                      ? 'text-emerald-700 bg-emerald-50 font-medium'
                      : 'text-gray-700 hover:bg-gray-50',
                  ]"
                >
                  <CheckIcon
                    v-if="isActiveSort(option)"
                    class="w-3.5 h-3.5 text-emerald-600 shrink-0"
                  />
                  <span v-else class="w-3.5 h-3.5 shrink-0" />
                  {{ option.label }}
                </button>
              </div>
            </Teleport>
          </div>
        </div>
      </div>
    </div>

    <!-- Workspace List -->
    <div
      data-mobile-panel-scroll
      :class="
        props.fillContainer
          ? 'flex-1 min-h-0 overflow-y-auto'
          : 'overflow-y-auto max-h-[calc(100vh-257px)]'
      "
    >
      <div v-if="authStore.isAuthenticated && isLoading" class="p-4 text-center text-gray-500">
        <div class="relative w-full h-8 mb-2 overflow-hidden">
          <BikeIcon v-if="showLoadingIcon" class="w-8 h-8 text-primary-500 animate-bike-ride" />
        </div>
        <p v-if="showLoadingIcon" class="text-sm">Workspaces loading...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="authStore.isAuthenticated && hasError" class="p-4 text-center text-gray-500">
        <SearchIcon class="w-8 h-8 mx-auto mb-2 text-red-300" />
        <p class="text-sm mb-3 text-red-600">
          Failed to load workspaces. Please check your connection and try again.
        </p>
        <button
          @click="loadWorkspaces"
          class="px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 hover:cursor-pointer"
          :disabled="isLoading"
        >
          {{ isLoading ? 'Loading...' : 'Retry' }}
        </button>
      </div>

      <!-- Empty State -->
      <BaseEmptyState
        v-else-if="authStore.isAuthenticated && displayedResults.length === 0"
        :icon="SearchIcon"
        title="No collections available"
      >
        <BaseButton
          v-if="authStore.isAuthenticated"
          size="sm"
          :loading="isLoading"
          @click="loadWorkspaces"
        >
          {{ isLoading ? 'Loading...' : 'Refresh' }}
        </BaseButton>
      </BaseEmptyState>

      <BaseEmptyState
        v-if="!authStore.isAuthenticated && !isLoading"
        :icon="SearchIcon"
        title="Explore your Collections."
      >
        <BaseButton size="md" @click="openLoginModal">Log in or sign up</BaseButton>
      </BaseEmptyState>

      <ul v-else ref="resultsListRef">
        <li
          v-for="result in displayedResults"
          :key="result.clientId"
          :data-workspace-id="result.clientId"
          class="relative overflow-hidden border-b border-gray-200 transition-colors hover:cursor-pointer"
          @click="handleResultClick(result)"
          @touchstart="onRowTouchStart($event, result)"
          @touchmove="onRowTouchMove($event, result)"
          @touchend="onRowTouchEnd(result)"
          @touchcancel="onRowTouchCancel(result)"
          @mouseenter="handleRowMouseEnter(result.clientId)"
          @mouseleave="handleRowMouseLeave(result.clientId)"
        >
          <div
            class="absolute inset-0 flex items-center justify-between border-y border-amber-200/70 bg-amber-500 px-5 text-white"
            aria-hidden="true"
          >
            <div class="flex items-center gap-3">
              <component
                :is="isCollectionPinned(result) ? PinOffIcon : PinIcon"
                class="h-5 w-5 shrink-0"
              />
              <span class="text-xs font-semibold leading-tight">
                {{ isCollectionPinned(result) ? 'Unpin' : 'Pin' }}
              </span>
            </div>
            <div class="flex items-center gap-3">
              <span class="text-xs font-semibold leading-tight">
                {{ isCollectionPinned(result) ? 'Unpin' : 'Pin' }}
              </span>
              <component
                :is="isCollectionPinned(result) ? PinOffIcon : PinIcon"
                class="h-5 w-5 shrink-0"
              />
            </div>
          </div>
          <div
            class="relative flex min-h-[88px] items-stretch px-3 py-3 transition-colors"
            :class="
              searchWorkspaceStore.hoveredWorkspaceId === result.clientId
                ? 'bg-gray-200'
                : 'bg-white hover:bg-gray-100'
            "
            :style="{
              transform: `translateX(${collectionSwipe.translateX[result.clientId] ?? 0}px)`,
              transition: collectionSwipe.transitioning[result.clientId]
                ? 'transform 0.25s ease'
                : undefined,
            }"
            @transitionend="collectionSwipe.clearTransition(result)"
          >
            <!-- Cover photo thumbnail -->
            <div class="relative w-16 h-16 rounded overflow-hidden shrink-0 mr-3 self-center">
              <img
                v-if="result.coverPhotoThumbnailUrl"
                :src="result.coverPhotoThumbnailUrl"
                class="w-full h-full object-cover"
                alt=""
                @error="() => refreshCoverPhotoUrlsIfNeeded()"
              />
              <div
                v-else
                class="w-full h-full flex items-center justify-center"
                :style="getWorkspacePlaceholderStyle(result.clientId)"
              >
                <WaypointsIcon class="w-7 h-7" />
              </div>
              <div
                v-if="isCollectionPinned(result)"
                class="absolute left-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-white/90 text-amber-600 shadow-sm"
                title="Pinned collection"
              >
                <PinIcon class="h-3 w-3 fill-current" />
              </div>
              <button
                v-if="props.selectionMode"
                type="button"
                class="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full border border-white bg-white text-emerald-700 shadow-sm"
                :class="{ 'text-transparent': !props.selectedCollectionIds?.has(result.clientId) }"
                aria-label="Select collection"
                @click.stop="emit('toggle-selection', result.clientId)"
              >
                <CheckIcon class="h-3.5 w-3.5" />
              </button>
            </div>
            <div class="flex-1 min-w-0 pr-2 self-center">
              <div class="flex items-center gap-1.5 min-w-0">
                <h3 class="text-base font-semibold leading-tight text-gray-900 truncate min-w-0">
                  <template v-if="editingWorkspaceId === result.clientId">
                    <input
                      v-auto-focus
                      v-model="editingWorkspaceName"
                      type="text"
                      class="w-full text-base font-semibold text-gray-900 bg-white border border-emerald-400 rounded px-1.5 py-0.5"
                      @click.stop
                      @keydown.enter.prevent="saveWorkspaceRename"
                      @keydown.esc.prevent="cancelInlineRename"
                    />
                  </template>
                  <template v-else>
                    {{ result.title }}
                  </template>
                </h3>
                <UsersIcon
                  v-if="result.hasWorkspaceRole && !result.isOwner"
                  class="w-3.5 h-3.5 text-gray-900 shrink-0"
                  title="Workspace shared with members"
                />
                <GlobeIcon
                  v-if="!result.isBookmarkOnly && result.isPublic"
                  class="w-3.5 h-3.5 text-gray-900 shrink-0"
                  title="Public workspace"
                />
                <BookmarkIcon
                  v-if="result.isBookmarkOnly"
                  class="w-3.5 h-3.5 text-gray-900 shrink-0"
                  title="Saved collection"
                />
                <span
                  v-if="getAuthorByline(result)"
                  class="text-sm font-normal text-gray-600 shrink-0"
                  >by {{ getAuthorByline(result) }}</span
                >
                <div
                  v-if="editingWorkspaceId === result.clientId"
                  class="flex items-center gap-1 shrink-0"
                >
                  <button
                    @click.stop="saveWorkspaceRename"
                    class="w-5 h-5 flex items-center justify-center rounded text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 hover:cursor-pointer transition-colors"
                    title="Save rename"
                  >
                    <CheckIcon class="w-3.5 h-3.5" />
                  </button>
                  <button
                    @click.stop="cancelInlineRename"
                    class="w-5 h-5 flex items-center justify-center rounded text-gray-500 hover:text-gray-700 hover:bg-gray-100 hover:cursor-pointer transition-colors"
                    title="Cancel rename"
                  >
                    <XIcon class="w-3.5 h-3.5" />
                  </button>
                </div>
                <button
                  v-if="isWorkspaceHidden(result.clientId)"
                  @click.stop="toggleWorkspaceVisibility(result.clientId)"
                  class="flex items-center justify-center w-5 h-5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 hover:cursor-pointer rounded transition-colors shrink-0"
                >
                  <EyeOffIcon class="w-3.5 h-3.5" />
                </button>
              </div>
              <div v-if="formatDateRange(result.activityStart, result.activityEnd)" class="mt-0.5">
                <template v-if="formatDateRange(result.activityStart, result.activityEnd)">
                  <span class="text-xs text-gray-600 shrink-0">{{
                    formatDateRange(result.activityStart, result.activityEnd)
                  }}</span>
                </template>
              </div>
              <div class="flex items-center gap-4 mt-1 min-h-[16px]">
                <span v-if="result.tourCount > 0" class="flex items-center gap-1">
                  <RouteIcon class="w-3 h-3 text-gray-600 shrink-0" />
                  <span class="text-xs text-gray-600">{{ result.tourCount }}</span>
                </span>
                <span v-if="result.poiCount > 0" class="flex items-center gap-1">
                  <WaypointsIcon class="w-3 h-3 text-gray-600 shrink-0" />
                  <span class="text-xs text-gray-600">{{ result.poiCount }}</span>
                </span>
                <span v-if="result.photoCount > 0" class="flex items-center gap-1">
                  <CameraIcon class="w-3 h-3 text-gray-600 shrink-0" />
                  <span class="text-xs text-gray-600">{{ result.photoCount }}</span>
                </span>

                <span
                  v-if="editingWorkspaceId === result.clientId && editingWorkspaceError"
                  class="text-xs text-red-600 truncate"
                >
                  {{ editingWorkspaceError }}
                </span>
              </div>
            </div>

            <div class="relative flex items-center shrink-0 ml-1" data-workspace-row-menu>
              <button
                v-if="!isSmallScreen"
                @click.stop="toggleRowMenu(result.clientId, $event)"
                class="flex items-center justify-center w-7 h-7 text-gray-500 hover:text-gray-700 hover:bg-gray-100 hover:cursor-pointer rounded-md transition-colors"
                title="Open workspace actions"
              >
                <MoreHorizontalIcon class="w-4 h-4" />
              </button>

              <Teleport to="body">
                <div
                  v-if="activeMenuWorkspaceId === result.clientId && !isSmallScreen"
                  :style="menuStyle"
                  data-workspace-row-menu
                  class="fixed z-9999 w-52 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden py-1"
                >
                  <CollectionActionSheetContent
                    :is-visible="isWorkspaceVisible(result.clientId)"
                    :is-bookmarked="result.isBookmarked"
                    :is-pinned="isCollectionPinned(result)"
                    :can-rename="canRenameWorkspaceResult(result)"
                    :can-delete="canDeleteWorkspaceResult(result)"
                    show-center-on-map
                    density="compact"
                    @share="openShareForWorkspace(result)"
                    @center-on-map="centerMapOnWorkspace(result.clientId)"
                    @toggle-visibility="toggleWorkspaceVisibility(result.clientId)"
                    @unbookmark="unbookmarkWorkspaceFromRow(result)"
                    @pin="toggleCollectionPinnedFromRow(result)"
                    @make-offline="openMakeOfflineDialog(result.clientId)"
                    @rename="openRenameWorkspace(result)"
                    @delete="deleteWorkspaceFromRow(result)"
                  />
                </div>
              </Teleport>
            </div>
          </div>
        </li>
      </ul>

      <MobileActionSheet
        v-if="activeMenuResult && isSmallScreen"
        :open="mobileCollectionMenuOpen"
        :title="activeMenuResult.title"
        :dot-color="getWorkspaceDotColor(activeMenuResult.clientId)"
        @close="closeRowMenu"
        @closed="handleMobileCollectionMenuClosed"
      >
        <CollectionActionSheetContent
          :is-visible="isWorkspaceVisible(activeMenuResult.clientId)"
          :is-bookmarked="activeMenuResult.isBookmarked"
          :is-pinned="isCollectionPinned(activeMenuResult)"
          :can-rename="canRenameWorkspaceResult(activeMenuResult)"
          :can-delete="canDeleteWorkspaceResult(activeMenuResult)"
          @share="openShareForWorkspace(activeMenuResult)"
          @toggle-visibility="toggleWorkspaceVisibility(activeMenuResult.clientId)"
          @unbookmark="unbookmarkWorkspaceFromRow(activeMenuResult)"
          @pin="toggleCollectionPinnedFromRow(activeMenuResult)"
          @make-offline="openMakeOfflineDialog(activeMenuResult.clientId)"
          @rename="openRenameWorkspace(activeMenuResult)"
          @delete="deleteWorkspaceFromRow(activeMenuResult)"
        />
      </MobileActionSheet>
    </div>
  </div>
</template>

<style scoped>
@keyframes bike-ride {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(calc(100vw - 2rem));
  }
}

.animate-bike-ride {
  animation: bike-ride 4s ease-in-out infinite;
}
</style>
